// Assembly Diagram PDF Generator
import jsPDF from 'jspdf';
import { RobotoRegularBase64 } from './fonts.js';

/**
 * Generate assembly diagram PDF for professional restorers
 * @param {Object} reconstructionData - Data from reconstruction process
 * @param {string} siteName - Name of the excavation site
 * @param {Array} fragments - Array of fragment objects with IDs and names
 * @param {boolean} returnBlob - If true, returns a Promise<Blob> instead of saving file
 */
export function generateAssemblyPDF(reconstructionData, siteName, fragments, returnBlob = false) {
    const doc = new jsPDF();
    
    // Add Custom Font for Turkish Characters
    doc.addFileToVFS('Roboto-Regular.ttf', RobotoRegularBase64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    // Map bold to the same regular font to avoid errors, or just use regular
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'bold');
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Title
    doc.setFontSize(22);
    doc.setTextColor(44, 62, 80); // Dark Blue
    doc.setFont('Roboto', 'bold');
    doc.text('Montaj Şeması / Assembly Diagram', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Visual Assembly Guide (Top)
    // If images are provided, show the first one (Main view) prominently
    if (reconstructionData && reconstructionData.images && reconstructionData.images.length > 0) {
        const mainImage = reconstructionData.images[0];

        // Calculate image dimensions to fit
        const imgWidth = pageWidth - (margin * 2);
        const imgHeight = 80; // Fixed height for main banner, or proportional

        try {
            doc.addImage(mainImage, 'PNG', margin, yPosition, imgWidth, imgHeight, undefined, 'FAST');
            doc.setDrawColor(200, 200, 200);
            doc.rect(margin, yPosition, imgWidth, imgHeight); // Border
            yPosition += imgHeight + 10;
        } catch (e) {
            console.error('Failed to add main image to PDF', e);
        }
    }

    doc.setTextColor(0, 0, 0); // Reset color

    // Site Information
    doc.setFontSize(12);
    doc.setFont('Roboto', 'normal');
    doc.text(`Kazı Alanı / Site: ${siteName}`, margin, yPosition);
    yPosition += 8;
    doc.text(`Tarih / Date: ${new Date().toLocaleDateString('tr-TR')}`, margin, yPosition);
    yPosition += 8;
    doc.text(`Toplam Parça / Total Parts: ${fragments.length}`, margin, yPosition);
    yPosition += 15;

    // Visual Assembly Guide (Global Views) Match 5-angle layout
    if (reconstructionData && reconstructionData.views) {
        const views = reconstructionData.views;

        doc.setFontSize(14);
        doc.setFont('Roboto', 'bold');
        doc.text('Genel Görünümler / Global Views', margin, yPosition);
        yPosition += 10;

        const rowHeight = 55;
        const colWidth3 = (pageWidth - (margin * 2) - 20) / 3; // For row of 3
        const colWidth2 = (pageWidth - (margin * 2) - 10) / 2; // For row of 2

        // Row 1: Top, Front, Right
        if (views.top) {
            doc.addImage(views.top, 'PNG', margin, yPosition, colWidth3, rowHeight);
            doc.setFontSize(10);
            doc.setFont('Roboto', 'normal');
            doc.text('Üst / Top', margin, yPosition + rowHeight + 5);
        }
        if (views.front) {
            doc.addImage(views.front, 'PNG', margin + colWidth3 + 10, yPosition, colWidth3, rowHeight);
            doc.text('Ön / Front', margin + colWidth3 + 10, yPosition + rowHeight + 5);
        }
        if (views.right) {
            doc.addImage(views.right, 'PNG', margin + (colWidth3 * 2) + 20, yPosition, colWidth3, rowHeight);
            doc.text('Sağ / Right', margin + (colWidth3 * 2) + 20, yPosition + rowHeight + 5);
        }
        yPosition += rowHeight + 15;

        // Row 2: Back, Isometric (Large)
        if (views.back) {
            doc.addImage(views.back, 'PNG', margin, yPosition, colWidth2, rowHeight);
            doc.text('Arka / Back', margin, yPosition + rowHeight + 5);
        }
        if (views.iso) {
            doc.addImage(views.iso, 'PNG', margin + colWidth2 + 10, yPosition, colWidth2, rowHeight);
            doc.text('İzometrik / Iso', margin + colWidth2 + 10, yPosition + rowHeight + 5);
        }
        yPosition += rowHeight + 15;
    }

    // Check space before inventory
    if (yPosition > pageHeight - 60) {
        doc.addPage();
        yPosition = margin;
    }

    // Section: Artifact Inventory
    doc.setFontSize(14);
    doc.setFont('Roboto', 'bold');
    doc.text('Eser Envanteri / Artifact Inventory', margin, yPosition);
    yPosition += 10;

    doc.setFontSize(10);
    doc.setFont('Roboto', 'normal');

    // Table Header
    doc.setFont('Roboto', 'bold');
    doc.text('Görsel/Img', margin, yPosition);
    doc.text('ID', margin + 30, yPosition);
    doc.text('Renk/Col', margin + 55, yPosition);
    doc.text('İsim/Name', margin + 85, yPosition);
    doc.text('Format', margin + 155, yPosition);
    yPosition += 7;

    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 5;

    // Use fragment data from reconstructionData if available (has images/colors), else fallback
    const fragmentList = (reconstructionData && reconstructionData.fragments) ? reconstructionData.fragments : fragments;

    doc.setFont('Roboto', 'normal');
    fragmentList.forEach((frag) => {
        if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = margin;
            // Header repeat
            doc.setFont('Roboto', 'bold');
            doc.text('Görsel/Img', margin, yPosition);
            doc.text('ID', margin + 30, yPosition);
            doc.text('Renk/Col', margin + 55, yPosition);
            doc.text('İsim/Name', margin + 85, yPosition);
            doc.text('Format', margin + 155, yPosition);
            yPosition += 12;
            doc.setFont('Roboto', 'normal');
        }

        // Image
        if (frag.image) {
            try {
                doc.addImage(frag.image, 'PNG', margin, yPosition, 25, 25);
            } catch (e) { }
        }
        const textY = yPosition + 12; // Center text vertically relative to 25px image

        doc.text(String(frag.artifactId || (frag.id || '-')), margin + 30, textY);

        // Color Swatch
        if (frag.colorHex) {
            const r = (frag.colorHex >> 16) & 255;
            const g = (frag.colorHex >> 8) & 255;
            const b = frag.colorHex & 255;
            doc.setFillColor(r, g, b);
            doc.rect(margin + 58, yPosition + 5, 15, 15, 'F');
            doc.setFillColor(0, 0, 0); // Reset
        }

        const name = (frag.name || 'Unnamed').substring(0, 25);
        doc.text(name, margin + 85, textY);
        doc.text((frag.format || 'OBJ').toUpperCase(), margin + 155, textY);

        yPosition += 30; // Row height for image
        doc.setDrawColor(240, 240, 240);
        doc.line(margin, yPosition - 2, pageWidth - margin, yPosition - 2);
    });

    yPosition += 10;

    // Section: Assembly Connections
    if (reconstructionData && reconstructionData.connections) {
        // Check if we need a new page
        if (yPosition > pageHeight - 50) {
            doc.addPage();
            yPosition = margin;
        }

        doc.setFontSize(14);
        doc.setFont('Roboto', 'bold');
        doc.text('Montaj Bağlantıları / Assembly Connections', margin, yPosition);
        yPosition += 10;

        doc.setFontSize(10);
        doc.setFont('Roboto', 'normal');

        reconstructionData.connections.forEach((connection, index) => {
            if (yPosition > pageHeight - 30) {
                doc.addPage();
                yPosition = margin;
            }

            const text = `${connection.from} ↔ ${connection.to}`;
            doc.text(text, margin + 10, yPosition);
            yPosition += 7;
        });
    } else {
        // If no connection data, add note
        doc.setFontSize(10);
        doc.setFont('Roboto', 'italic');
        doc.text('Bağlantı verisi bulunamadı / Connections data not available.', margin, yPosition);
        yPosition += 7;
        doc.text('Parçalar otomatik olarak hizalanmıştır / Fragments were assembled automatically.', margin, yPosition);
    }

    // Additional Views (if available)
    if (reconstructionData && reconstructionData.images && reconstructionData.images.length > 1) {
        yPosition += 15;

        // Check page break
        if (yPosition > pageHeight - 60) {
            doc.addPage();
            yPosition = margin;
        }

        doc.setFontSize(14);
        doc.setFont('Roboto', 'bold');
        doc.text('Ek Görünümler / Additional Views', margin, yPosition);
        yPosition += 10;

        // Grid layout for extra images
        const rowHeight = 60;
        const colWidth = (pageWidth - (margin * 2) - 10) / 2;

        let currentX = margin;

        for (let i = 1; i < reconstructionData.images.length; i++) {
            // Check vertical space
            if (yPosition + rowHeight > pageHeight - 20) {
                doc.addPage();
                yPosition = margin;
            }

            try {
                doc.addImage(reconstructionData.images[i], 'PNG', currentX, yPosition, colWidth, rowHeight, undefined, 'FAST');
                doc.setDrawColor(220, 220, 220);
                doc.rect(currentX, yPosition, colWidth, rowHeight);
            } catch (e) {
                console.error('Error adding extra image', e);
            }

            // Advance column
            if (currentX === margin) {
                currentX += colWidth + 10;
            } else {
                currentX = margin;
                yPosition += rowHeight + 10;
            }
        }
    }

    // Footer
    yPosition = pageHeight - 15;
    doc.setFontSize(8);
    doc.setFont('Roboto', 'italic');
    doc.text('ArchaeoHub Dijital Kazı Yönetim Sistemi / Digital Excavation Management System', pageWidth / 2, yPosition, { align: 'center' });

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${siteName.replace(/\s+/g, '_')}_assembly_${timestamp}.pdf`;

    if (returnBlob) {
        return doc.output('blob');
    }

    // Download
    doc.save(filename);
}

/**
 * Create simple assembly connections from fragment list
 * @param {Array} fragments - Array of fragments
 * @returns {Array} Array of connection objects
 */
export function createSimpleConnections(fragments) {
    const connections = [];

    // Create sequential connections (simplified)
    for (let i = 0; i < fragments.length - 1; i++) {
        connections.push({
            from: `Artifact ${fragments[i].artifactId || i}`,
            to: `Artifact ${fragments[i + 1].artifactId || (i + 1)}`
        });
    }

    return connections;
}
